/**
 * Locations — the admin-CRUD surface over the workspace's venue catalog.
 * `locationsState`/`LocationWithId` are NOT redeclared here — the canonical
 * read state is `storefront/locations.state.ts` (the public catalog every
 * event header and this admin surface alike resolve venues against;
 * locations.sync.ts, web-side, is its sole writer). This file adds only
 * what the admin surface needs on top: the chain-integrity checks on
 * `within`, the page/form view-models, and the write-intent channel every
 * location-CRUD view dispatches through — a second locations.sync.ts
 * (web-side) subscribes to that channel, performing the Firestore writes
 * and reconciling ok/error back keyed by requestId.
 */
import { computed } from "@mhmo91/schmancy/state";
import type { Dependents } from "@momo/types";
import { Subject } from "rxjs";
import { canManageEvents, eventsState } from "../events/events.state.ts";
import {
	type LocationWithId,
	locationsState,
	locationsStatus,
} from "../storefront/locations.state.ts";
import { effectiveBrandSeedColor } from "../workspace/workspace.state.ts";

export type { LocationWithId };

// ── Chain integrity — `within` cycles and self-reference ───────────────────

/**
 * True when pointing `id` (null for a not-yet-created location) at
 * `candidateWithin` would self-reference or complete a cycle through the
 * existing catalog's own `within` chain.
 */
export function withinCreatesCycle(
	id: string | null,
	candidateWithin: string,
	locations: readonly LocationWithId[],
): boolean {
	if (!candidateWithin) return false;
	if (id !== null && candidateWithin === id) return true;
	const seen = new Set<string>();
	let cursor: string | undefined = candidateWithin;
	while (cursor) {
		if (id !== null && cursor === id) return true;
		if (seen.has(cursor)) return true; // a pre-existing cycle in the data — refuse rather than loop forever
		seen.add(cursor);
		cursor = locations.find((l) => l.id === cursor)?.within ?? undefined;
	}
	return false;
}

// ── Page view-model ────────────────────────────────────────────────────────

export const locationsPageView = computed(() => {
	const locations = locationsState.value;
	const rows = locations
		.map((l) => {
			// A room without its own pin is physically at its nearest mapped
			// ancestor's coordinates — walk `within` (cycle-safe, mirroring
			// withinCreatesCycle's guard) until one carries `geo`.
			let effectiveGeo = l.geo ?? null;
			let geoInheritedFromName: string | null = null;
			const seen = new Set<string>();
			let cursor = l.within;
			while (!effectiveGeo && cursor && !seen.has(cursor)) {
				seen.add(cursor);
				const parent = locations.find((p) => p.id === cursor);
				if (!parent) break;
				if (parent.geo) {
					effectiveGeo = parent.geo;
					geoInheritedFromName = parent.name;
					break;
				}
				cursor = parent.within;
			}
			return {
				id: l.id,
				name: l.name,
				address: l.address,
				geo: l.geo ?? null,
				effectiveGeo,
				geoInheritedFromName,
				withinName: locations.find((p) => p.id === l.within)?.name ?? null,
				usedByCount: eventsState.value.filter((e) => e.locationId === l.id)
					.length,
			};
		})
		.toSorted((a, b) => a.name.localeCompare(b.name));
	return { rows };
});

/** The resolved catalog row an id points at, or undefined when unset or not
 *  found — select-location.view.ts's own join, so its trigger reads a
 *  computed row rather than deriving one from raw state inline. */
export function resolveLocationRow(id: string) {
	return locationsPageView.get().rows.find((r) => r.id === id);
}

/**
 * Locations-index phase — the admin CRUD surface's own gate on
 * `canManageEvents`, then the catalog's own load state. Unlike events, a
 * location's own Firestore read is public (Location's `can('read')` needs no
 * permission — the storefront resolves venues with no auth), so "denied"
 * here means the actor may not manage the catalog, not a rejected read.
 * "loading"/"error" mirror locationsStatus (storefront/locations.state.ts),
 * itself mirroring eventsListState/memberListState's shape.
 */
export const locationsListState = computed<
	"loading" | "error" | "denied" | "empty" | "populated"
>(() => {
	if (!canManageEvents.get()) return "denied";
	if (locationsStatus.value === "loading") return "loading";
	if (locationsStatus.value === "error") return "error";
	return locationsPageView.get().rows.length === 0 ? "empty" : "populated";
});

/**
 * Static-map marker accent for the location-list grid's map-hero tiles —
 * the workspace's effective brand seed, stripped of its leading "#" for the
 * Static Maps API's own hex format.
 */
export const locationMapMarkerColor = computed(() =>
	effectiveBrandSeedColor.get().replace(/^#/, ""),
);

/** Candidate `within` parents for editing `id` (null when creating) — excludes self and anything that would cycle. */
export function withinOptions(id: string | null): readonly LocationWithId[] {
	return locationsState.value.filter(
		(l) => !withinCreatesCycle(id, l.id, locationsState.value),
	);
}

// ── write-intent channel ────────────────────────────────────────────────────
// Every write a location-CRUD view needs is zero-I/O intent composed here
// and pushed to locationsAction$; locations.sync.ts (web-side) is the sole
// subscriber, performing the Firestore write and reconciling ok/error back
// via locationsResult$, keyed by requestId.

export type LocationWrite = {
	name: string;
	address: string;
	geo: { lat: number; lng: number } | null;
	url: string | null;
	notes: string | null;
	within: string | null;
	timezone: string;
};

export type LocationsAction =
	| { kind: "create"; requestId: string; body: LocationWrite }
	| { kind: "update"; requestId: string; id: string; body: LocationWrite }
	| { kind: "delete"; requestId: string; id: string };

export const locationsAction$ = new Subject<LocationsAction>();

export interface LocationsResult {
	readonly requestId: string;
	readonly kind: LocationsAction["kind"];
	readonly ok: boolean;
	/** the generated doc id — carried on a successful 'create' result */
	readonly id?: string;
	readonly error?: string;
	/** The census the delete door read, present only on a REFUSED delete —
	 * what still names the document, in counts. Its presence is the
	 * discriminant: a refusal is a normal answer with real numbers, not a
	 * failure, so `ok` is false and `error` is absent, and the form opens
	 * <delete-refusal> over this rather than a generic toast. */
	readonly dependents?: Dependents.Dependents;
}

export const locationsResult$ = new Subject<LocationsResult>();
